import {
  TextInput,
  SelectInput,
  BooleanInput,
  ArrayInput,
  SimpleFormIterator,
  required,
  useTranslate,
} from 'react-admin';
import { Box, Card, CardContent, Typography } from '@mui/material';
import {
  ROOT_CAUSE_CATEGORIES,
  DIAGNOSTIC_STEPS_EXAMPLE,
  formatJson,
  parseJsonInput,
  validateJsonString,
} from './playbookFormHelpers';

// Shared SimpleForm body used by both PlaybookCreate and PlaybookEdit. Editable fields map 1:1 to
// REF-01 s.1.2 columns. Read-only fields (id, createdFromInvestigationId, createdBy, createdAt,
// updatedAt) are never rendered here, so they never enter the form payload. Layout uses MUI v7 Box
// flexbox (no Grid). The transform on Create/Edit JSON-parses the two JSON string inputs.
export const PlaybookForm = () => {
  const translate = useTranslate();

  const categoryChoices = ROOT_CAUSE_CATEGORIES.map((c) => ({
    id: c,
    name: translate(`resources.ai-playbooks.categories.${c}`),
  }));

  return (
    <Box sx={{ width: '100%', maxWidth: 980, '& .MuiFormControl-root': { width: '100%' } }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Left column: identity, category, active */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {translate('resources.ai-playbooks.sections.identity')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextInput
                  source="name"
                  label={translate('resources.ai-playbooks.fields.name')}
                  validate={[required()]}
                  fullWidth
                />
                <TextInput
                  source="description"
                  label={translate('resources.ai-playbooks.fields.description')}
                  multiline
                  minRows={2}
                  fullWidth
                />
                <SelectInput
                  source="rootCauseCategory"
                  label={translate('resources.ai-playbooks.fields.rootCauseCategory')}
                  choices={categoryChoices}
                  emptyText={translate('resources.ai-playbooks.categories.none')}
                  fullWidth
                  helperText={translate('resources.ai-playbooks.helpers.rootCauseCategory')}
                />
                <BooleanInput
                  source="isActive"
                  label={translate('resources.ai-playbooks.fields.isActive')}
                  defaultValue={true}
                  helperText={translate('resources.ai-playbooks.helpers.isActive')}
                />
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {translate('resources.ai-playbooks.sections.symptoms')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <ArrayInput
                  source="symptomKeywords"
                  label={translate('resources.ai-playbooks.fields.symptomKeywords')}
                  helperText={translate('resources.ai-playbooks.helpers.symptomKeywords')}
                >
                  <SimpleFormIterator inline>
                    <TextInput
                      source=""
                      label={translate('resources.ai-playbooks.fields.keyword')}
                      validate={[required()]}
                      helperText={false}
                    />
                  </SimpleFormIterator>
                </ArrayInput>
                <ArrayInput
                  source="symptomExamples"
                  label={translate('resources.ai-playbooks.fields.symptomExamples')}
                  helperText={translate('resources.ai-playbooks.helpers.symptomExamples')}
                >
                  <SimpleFormIterator>
                    <TextInput
                      source=""
                      label={translate('resources.ai-playbooks.fields.example')}
                      multiline
                      helperText={false}
                    />
                  </SimpleFormIterator>
                </ArrayInput>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Right column: remediation + JSON recipe fields */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {translate('resources.ai-playbooks.sections.remediation')}
              </Typography>
              <TextInput
                source="remediationGuidance"
                label={translate('resources.ai-playbooks.fields.remediationGuidance')}
                multiline
                minRows={3}
                fullWidth
                helperText={translate('resources.ai-playbooks.helpers.remediationGuidance')}
              />
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                {translate('resources.ai-playbooks.sections.diagnostics')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextInput
                  source="diagnosticSteps"
                  label={translate('resources.ai-playbooks.fields.diagnosticSteps')}
                  multiline
                  minRows={6}
                  fullWidth
                  format={formatJson}
                  parse={parseJsonInput}
                  validate={validateJsonString}
                  placeholder={DIAGNOSTIC_STEPS_EXAMPLE}
                  helperText={translate('resources.ai-playbooks.helpers.diagnosticSteps')}
                  sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
                <TextInput
                  source="triggerConditions"
                  label={translate('resources.ai-playbooks.fields.triggerConditions')}
                  multiline
                  minRows={3}
                  fullWidth
                  format={formatJson}
                  parse={parseJsonInput}
                  validate={validateJsonString}
                  placeholder='{ "anchorType": "charger" }'
                  helperText={translate('resources.ai-playbooks.helpers.triggerConditions')}
                  sx={{ '& textarea': { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default PlaybookForm;
